/*
 * webviewPolyfills.ts - Polyfills and defensive checks for WebView environments
 *
 * This module provides polyfills and safety checks for WebView-specific issues,
 * particularly around bridge disconnections and missing APIs.
 */

// Extended window interface for WebView detection
interface WebViewWindow extends Window {
  webkit?: {
    messageHandlers?: unknown;
  };
  AndroidInterface?: unknown;
}

// WebView environment detection
export const isWebView = (): boolean => {
  try {
    if (typeof window === "undefined") return false;

    const userAgent = navigator.userAgent || "";
    const webViewWindow = window as WebViewWindow;

    return (
      userAgent.includes("wv") || // Android WebView
      userAgent.includes("WebView") ||
      userAgent.includes("FBANApp") || // Facebook App WebView
      userAgent.includes("FBAV") || // Facebook App
      userAgent.includes("Instagram") ||
      !!webViewWindow.webkit?.messageHandlers || // iOS WKWebView
      !!webViewWindow.AndroidInterface || // Android Bridge
      false
    );
  } catch (error) {
    console.warn("Error detecting WebView environment:", error);
    return false;
  }
};

// Safe navigator.language access
export const getSafeLanguage = (): string => {
  try {
    if (typeof navigator !== "undefined" && navigator.language) {
      return navigator.language.split("-")[0];
    }
  } catch (error) {
    console.warn("Error accessing navigator.language:", error);
  }
  return "en"; // Default fallback
};

// Safe localStorage access
export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      if (typeof localStorage !== "undefined" && localStorage) {
        return localStorage.getItem(key);
      }
    } catch (error) {
      console.warn(`Error accessing localStorage.getItem(${key}):`, error);
    }
    return null;
  },

  setItem: (key: string, value: string): boolean => {
    try {
      if (typeof localStorage !== "undefined" && localStorage) {
        localStorage.setItem(key, value);
        return true;
      }
    } catch (error) {
      console.warn(`Error accessing localStorage.setItem(${key}):`, error);
    }
    return false;
  },

  removeItem: (key: string): boolean => {
    try {
      if (typeof localStorage !== "undefined" && localStorage) {
        localStorage.removeItem(key);
        return true;
      }
    } catch (error) {
      console.warn(`Error accessing localStorage.removeItem(${key}):`, error);
    }
    return false;
  },
};

// Install WebView polyfills and error handlers
export const initWebViewPolyfills = (): void => {
  if (typeof window === "undefined") return;

  // Global error handler for unhandled WebView bridge errors
  window.addEventListener(
    "error",
    event => {
      if (event.error?.message?.includes("Java object is gone")) {
        console.warn("Caught WebView bridge error:", event.error.message);
        event.preventDefault(); // Prevent default error handling
      }
    },
    { passive: true }
  );

  // Unhandled promise rejection handler
  window.addEventListener(
    "unhandledrejection",
    event => {
      if (event.reason?.message?.includes("Java object is gone")) {
        console.warn(
          "Caught WebView bridge promise rejection:",
          event.reason.message
        );
        event.preventDefault(); // Prevent default error handling
      }
    },
    { passive: true }
  );

  // Log WebView environment info
  if (isWebView()) {
    const webViewWindow = window as WebViewWindow;
    console.info("WebView environment detected:", {
      userAgent: navigator.userAgent,
      hasWebkitMessageHandlers: !!webViewWindow.webkit?.messageHandlers,
      hasAndroidInterface: !!webViewWindow.AndroidInterface,
    });
  }
};

export default {
  isWebView,
  getSafeLanguage,
  safeLocalStorage,
  initWebViewPolyfills,
};
